// Every string the search drawer's pushed filter screens can render, and the
// shape a caller may replace them with.
//
// NAMED FOR THE PAIR, NOT FOR ONE SCREEN. RangeSliderScreen and
// SelectionListScreen are the same pushed frame twice — the same Back to
// `Filters`, the same `Clear`, the same `Apply` — and they render those three
// words identically. Both defaults objects below are built from
// FILTER_SCREEN_TRANSLATIONS for that reason; a second translations module
// beside this one would make the mechanism the duplication it was introduced to
// prevent.
//
// The mechanism is BidCard's exactly — one object of finished text in one
// language, merged over these defaults key by key, with an UNKNOWN key rejected
// because that is the failure that renders English forever with nothing saying
// why. The reasoning is written out once, in bidCardTranslations.js.
//
// What is worth saying here is why the screens cannot simply hardcode their
// English. The app around them translates everything else through `vue-i18n`,
// and its own catalog already carries these phrases in both languages:
// `search.drawer.priceRange` is `"${min}k – ${max}k"` in English and
// `"{min} k$ – {max} k$"` in French, because French puts the currency mark
// AFTER the figure. A component that hardcodes a leading `$` and composes a
// number after it cannot be translated by any prop short of the whole template,
// so the whole phrase is the unit of translation here rather than the number.
//
// Deliberately NOT exported from the package barrel: the catalog gate holds
// that a barrel exports components and nothing else.

import { unreadTranslationKeys as unreadKeysAgainst } from './bidCardTranslations';

export { interpolate } from './bidCardTranslations';

/**
 * The frame both filter screens draw, frozen.
 *
 * Back leads to the drawer, which is titled Filters — so the control is named
 * for where it goes rather than for the arrow it draws.
 */
export const FILTER_SCREEN_TRANSLATIONS = Object.freeze({
  filtersBack: 'Filters',
  clear: 'Clear',
  apply: 'Apply',
});

/**
 * Everything RangeSliderScreen can render, frozen.
 *
 * `{name}` placeholders are filled by the screen. A translation may move them
 * around its sentence — which is the point of the price and mileage rows — but
 * it may not invent one, since the screen only substitutes the names it knows.
 *
 * The `k` is NOT in these templates, and that is deliberate. A track value is
 * compact only when it is a whole thousand (`formatThousands`), so a template
 * carrying its own `k` would render a mileage ceiling of 400 as "Under 400k mi".
 * The figure arrives already compacted; the template contributes the currency
 * mark, the unit and the separator, which are the parts a language moves.
 */
export const RANGE_SLIDER_SCREEN_TRANSLATIONS = Object.freeze({
  ...FILTER_SCREEN_TRANSLATIONS,

  // The two numeric fields of the year screen. Each is the visible text of a
  // <label> wrapping its input, so these are the fields' accessible names as
  // well as their captions.
  minimumInput: 'Min',
  maximumInput: 'Max',

  // The accessible names of the slider thumbs. A dual track has two, and
  // without these they are indistinguishable to a screen reader — which is
  // the `aria-input-field-name` violation that blocked this component's
  // promotion. The single-thumb mileage track moves a ceiling, so it takes
  // `maximumThumb`.
  minimumThumb: 'Minimum',
  maximumThumb: 'Maximum',

  // The year readout, in its four branches. "Any" is a real answer here and
  // not the widest range: it says the dealer does not care about the year.
  anyYear: 'Any year',
  yearSpan: '{min} – {max}',
  yearFrom: '≥ {min}',
  yearTo: '≤ {max}',

  // The mileage readout, and one thumb's `aria-valuetext`. Two templates
  // rather than one, because a bound read out on its own is not the sentence
  // the screen shows above it.
  anyMileage: 'Any mileage',
  mileageCeiling: 'Under {value} mi',
  mileageValue: '{value} mi',

  // The price readout, and one thumb's `aria-valuetext`. These are the two
  // rows the French catalog already disagrees with the component about.
  priceSpan: '${min} – ${max}',
  priceValue: '${value}',
});

/**
 * Everything SelectionListScreen can render, frozen.
 *
 * `{name}` placeholders are filled by the screen, on the same terms as above.
 */
export const SELECTION_LIST_SCREEN_TRANSLATIONS = Object.freeze({
  ...FILTER_SCREEN_TRANSLATIONS,

  // The search field's placeholder AND its visually hidden label — one string
  // for both, because a placeholder disappears the moment anybody types and the
  // label is the same sentence said where it survives. `{title}` arrives
  // lower-cased, as the screen has always rendered it.
  searchPlaceholder: 'Search {title}',
  // The accessible name of the × inside the field. NOT the drawer's own
  // `closeAriaLabel`, which reads "Close filters": this empties the query and
  // closes nothing, so reusing that string would announce the wrong action in
  // both languages.
  clearSearch: 'Clear search',
  // A chip's accessible name. Without it a chip and its row are both announced
  // as "Ford, button" while doing opposite things.
  removeChip: 'Remove {label}',

  // The Apply button, in its four branches: single-select shows no count, and
  // multi-select picks one of the three below.
  //
  // THREE COUNT KEYS, AND ZERO IS WHY. The button reads zero every time the
  // screen opens with nothing chosen and every time Clear is pressed, and the
  // two target locales disagree about which form that takes — `en-CA` puts 0 in
  // `other`, `fr-CA` puts it in `one`. Giving zero its own key removes the
  // disagreement rather than resolving it, because the remaining choice, one
  // against several, is the same category in both. That is what lets the screen
  // choose without knowing which locale it is in.
  //
  // All three read identically in English, which is the honest answer: English
  // does not inflect here. The keys exist for the languages that do. Their
  // limit is languages whose categories collapse to zero, one and the rest — a
  // dual or paucal form would need a fourth key and a locale to pick it.
  applyNoneSelected: 'Apply · {count} selected',
  applyOneSelected: 'Apply · {count} selected',
  applySeveralSelected: 'Apply · {count} selected',

  // The two empty states, which are two different facts. A list the query
  // excluded is the dealer's own doing; a list with nothing in it is not, and
  // reporting it as a failed search told a dealer their empty query matched
  // nothing on every Model and Trim screen opened before a search had run.
  noMatches: 'No matches for “{query}”',
  nothingToChoose: 'Nothing to choose from',
});

/**
 * The keys in `translations` that RangeSliderScreen would not read, as dotted
 * paths.
 *
 * The walk itself is BidCard's — it already takes the defaults object as an
 * argument, so this passes its own rather than owning a second copy of a
 * comparison that has to behave identically on every surface.
 */
export const unreadRangeSliderScreenTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, RANGE_SLIDER_SCREEN_TRANSLATIONS);

/** The keys in `translations` that SelectionListScreen would not read. */
export const unreadSelectionListScreenTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, SELECTION_LIST_SCREEN_TRANSLATIONS);

/** One language's text over the English defaults, key by key. */
export function mergeRangeSliderScreenTranslations(translations) {
  if (!translations) return RANGE_SLIDER_SCREEN_TRANSLATIONS;

  return { ...RANGE_SLIDER_SCREEN_TRANSLATIONS, ...translations };
}

/**
 * The `i18n` prop's validator: `{ translations }`, optional.
 *
 * There is no `formatMoney` here, and its absence is a decision rather than an
 * omission. The price readout's rule is the screen's own — compact when the
 * figure is a whole thousand, the full number otherwise — and handing it a
 * currency formatter would put the readout and the payload back in a position
 * to disagree, which is the defect this pass removed.
 *
 * Missing keys are what the merge is for. This catches the two things the merge
 * cannot: a key the screen never reads, and `translation` written for
 * `translations`, which fails exactly the way a mis-keyed string does —
 * silently, in English.
 */
export function isValidRangeScreenI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') return false;
  if (Object.keys(i18n).some((key) => key !== 'translations')) return false;
  if (i18n.translations === undefined) return true;
  return unreadRangeSliderScreenTranslationKeys(i18n.translations).length === 0;
}

/** One language's text over the English defaults, key by key. */
export function mergeSelectionListScreenTranslations(translations) {
  if (!translations) return SELECTION_LIST_SCREEN_TRANSLATIONS;

  return { ...SELECTION_LIST_SCREEN_TRANSLATIONS, ...translations };
}

/**
 * The `i18n` prop's validator: `{ translations }`, optional.
 *
 * The same two failures the merge cannot catch — a key the screen never reads,
 * and `translation` written for `translations`, both of which render English
 * forever with nothing saying why.
 */
export function isValidSelectionListScreenI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') return false;
  if (Object.keys(i18n).some((key) => key !== 'translations')) return false;
  if (i18n.translations === undefined) return true;
  return unreadSelectionListScreenTranslationKeys(i18n.translations).length === 0;
}
